/** @type {import('tailwindcss').Config} */
import typography from '@tailwindcss/typography';
import animate from 'tailwindcss-animate';

const config = {
    darkMode: 'class',
    content: ['./src/**/*.{js,jsx,ts,tsx}'],
    corePlugins: {
        // disables the particular css normalizing that happens.
        // https://tailwindcss.com/docs/preflight
        preflight: false,
    },
    theme: {
        colors: {
            transparent: 'transparent',
            current: 'currentColor',
            black: 'black',
            white: '#ffffff',
            // replace existing tw colors here
            gray: {
                0: '#f8fafe',
                25: '#f8f8f8',
                50: '#e9edf3',
                100: '#dbe0e9',
                200: '#ccd3de',
                300: '#b9c3d1',
                400: '#a7b3c4',
                500: '#94a3b7',
                600: '#838e9e',
                700: '#727a86',
                800: '#62676e',
                900: '#40454b',
                950: '#20252b',
                1000: '#000000',
            },
        },
        extend: {
            // extend to add new colors
            colors: {
                cerulean: {
                    0: '#f2f8fb',
                    50: '#d7eaf2',
                    80: '#1A5A7D',
                    90: '#0A4461',
                    100: '#aed6e5',
                    200: '#92c8dd',
                    300: '#73bbd4',
                    400: '#4eadcc',
                    500: '#07a0c3',
                    600: '#0089ab',
                    700: '#007394',
                    800: '#005d7d',
                    900: '#004868',
                    950: '#002b48',
                    1000: '#001b34',
                },
                bluewood: {
                    0: '#eff0f2',
                    50: '#ced2d8',
                    300: '#647081',
                    500: '#2c3e53',
                    1000: '#00091f',
                },
                sienna: {
                    0: '#fff4f0',
                    50: '#ffdfd4',
                    100: '#ffbfaa',
                    400: '#f27f5a',
                    500: '#ea6940',
                    600: '#cf522c',
                    700: '#b43b17',
                    800: '#9a2301',
                    950: '#5d0000',
                    1000: '#490000',
                },
                buttercup: {
                    0: '#fff8f0',
                    50: '#ffebd2',
                    100: '#ffd8a5',
                    200: '#ffcb87',
                    300: '#fdbf69',
                    400: '#f9b24a',
                    500: '#f3a623',
                    600: '#d28a00',
                    700: '#b26f00',
                    800: '#925600',
                    900: '#753d00',
                    950: '#4f1900',
                    1000: '#3d0000',
                },
                aqua: {
                    0: '#f4fbf8',
                    5: '#F5FCFA',
                    50: '#def4eb',
                    70: '#2D7D5C',
                    100: '#bde9d8',
                    200: '#a6e1cb',
                    300: '#8fd9be',
                    400: '#75d1b1',
                    500: '#59c9a5',
                    600: '#38ac89',
                    700: '#068f6e',
                    800: '#007455',
                    900: '#00593c',
                    950: '#00331a',
                    1000: '#002100',
                },
                green: {
                    200: '#80CE5B',
                },
                purple: {
                    0: '#fef5fe',
                    50: '#f3e5f5',
                    100: '#eed2f3',
                    200: '#e1bee7',
                    300: '#d9a7e1',
                    400: '#d08adb',
                    500: '#cc77db',
                    600: '#b35fc1',
                    700: '#ab47bc',
                    800: '#9528a8',
                    900: '#740f8e',
                    950: '#4a0972',
                    1000: '#370058',
                },
            },
            spacing: {
                px: '1px',
                0: '0px',
                0.5: '2px',
                1: '4px',
                1.5: '6px',
                2: '8px',
                2.5: '10px',
                3: '12px',
                3.5: '14px',
                4: '16px',
                5: '20px',
                6: '24px',
                7: '28px',
                8: '32px',
                9: '36px',
                10: '40px',
                11: '44px',
                12: '48px',
                14: '56px',
                16: '64px',
                20: '80px',
                24: '96px',
                28: '112px',
                32: '128px',
                36: '144px',
                40: '160px',
                44: '176px',
                48: '192px',
                52: '208px',
                56: '224px',
                60: '240px',
                64: '256px',
                72: '288px',
                80: '320px',
                96: '384px',
                'sidebar-width': '200px',
            },
            fontSize: {
                sm: ['12px', '16px'],
                base: ['14px', '20px'],
                lg: ['16px', '24px'],
                xl: ['20px', '28px'],
                '2xl': ['26px', '32px'],
            },
            screens: {
                sm: '768px', // $screen-sm-min
                md: '992px', // $screen-md-min
                lg: '1200px', // $screen-lg-min
            },
            boxShadow: {
                'gray-2dp':
                    '0px 0px 2px 0px rgba(98, 103, 110, 0.24), 0px 0px 4px 0px rgba(131, 142, 158, 0.24)',
                'gray-4dp':
                    '0px 0px 2px rgba(98, 103, 110, 0.24), 0px 0px 4px rgba(131, 142, 158, 0.24)',
                'gray-4dp-ds':
                    '0px 0px 4px 0px rgba(98, 103, 110, 0.2), 0px 0px 8px 0px rgba(131, 142, 158, 0.2)',
                'gray-24dp': '0px 0px 48px 0px #838E9E14, 0px 0px 24px 0px #62676E14',
            },
            keyframes: {
                'fade-in': {
                    from: { opacity: 0 },
                    to: { opacity: 1 },
                },
                'fade-out': {
                    from: { opacity: 1 },
                    to: { opacity: 0 },
                },
                'slide-in-from-right': {
                    from: { transform: 'translateX(100%)' },
                    to: { transform: 'translateX(0)' },
                },
                'slide-in-from-left': {
                    from: { transform: 'translateX(-100%)' },
                    to: { transform: 'translateX(0)' },
                },
                'slide-in-from-top': {
                    from: { transform: 'translateY(-100%)' },
                    to: { transform: 'translateY(0)' },
                },
                'slide-in-from-bottom': {
                    from: { transform: 'translateY(100%)' },
                    to: { transform: 'translateY(0)' },
                },
                'slide-out-to-right': {
                    from: { transform: 'translateX(0)' },
                    to: { transform: 'translateX(100%)' },
                },
                'slide-out-to-left': {
                    from: { transform: 'translateX(0)' },
                    to: { transform: 'translateX(-100%)' },
                },
                'slide-out-to-top': {
                    from: { transform: 'translateY(0)' },
                    to: { transform: 'translateY(-100%)' },
                },
                'slide-out-to-bottom': {
                    from: { transform: 'translateY(0)' },
                    to: { transform: 'translateY(100%)' },
                },
            },
        },
    },
    plugins: [typography, animate],
};

export default config;
